/**
 * CMS API client
 * --------------------------------------------------------------------
 * Centralized fetch wrapper for the inline CMS admin shell.
 *
 * Reads:
 *   - Base URL from process.env.REACT_APP_CMS_API_BASE
 *     (fallback: http://localhost/School/backend)
 *   - Bearer token from localStorage.cms_token
 *
 * All functions throw { status, message, raw } on non-2xx responses.
 */

const DEFAULT_BASE = 'http://localhost:8000';

export function getApiBase() {
  const env =
    typeof process !== 'undefined' && process.env
      ? process.env.REACT_APP_CMS_API_BASE
      : null;
  const base = env || DEFAULT_BASE;
  return base.replace(/\/+$/, '');
}

export function getToken() {
  try {
    return typeof localStorage !== 'undefined'
      ? localStorage.getItem('cms_token')
      : null;
  } catch (_) {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem('cms_token', token);
    else localStorage.removeItem('cms_token');
  } catch (_) {
    /* ignore */
  }
}

function buildUrl(path) {
  const base = getApiBase();
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

async function parseResponse(res) {
  const contentType = res.headers.get('content-type') || '';
  let body = null;
  if (contentType.includes('application/json')) {
    try {
      body = await res.json();
    } catch (_) {
      body = null;
    }
  } else {
    try {
      body = await res.text();
    } catch (_) {
      body = null;
    }
  }
  return body;
}

async function request(path, { method = 'GET', body, headers = {}, isForm = false, auth = false } = {}) {
  const finalHeaders = { ...headers };
  if (!isForm && body !== undefined) {
    finalHeaders['Content-Type'] = 'application/json';
  }
  if (auth) {
    const token = getToken();
    if (token) finalHeaders['Authorization'] = `Bearer ${token}`;
  }

  let payload;
  if (isForm) {
    payload = body;
  } else if (body !== undefined) {
    payload = JSON.stringify(body);
  }

  let res;
  try {
    res = await fetch(buildUrl(path), {
      method,
      headers: finalHeaders,
      body: payload,
    });
  } catch (err) {
    const networkErr = new Error(err && err.message ? err.message : 'Network error');
    networkErr.status = 0;
    networkErr.raw = err;
    throw networkErr;
  }

  const data = await parseResponse(res);

  if (!res.ok) {
    const message =
      (data && typeof data === 'object' && (data.message || data.error)) ||
      (typeof data === 'string' && data) ||
      `Request failed with status ${res.status}`;
    const apiErr = new Error(message);
    apiErr.status = res.status;
    apiErr.raw = data;
    throw apiErr;
  }

  // Backend wraps successful responses as { status, message, data: {...} }.
  // Unwrap so callers receive the inner payload directly.
  if (data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, 'data') && (data.status === 'success' || data.status === undefined)) {
    return data.data;
  }
  return data;
}

/* ----------------------------- AUTH ----------------------------- */

export function login({ username, password, fingerprint, deviceCookie }) {
  return request('/api/website-auth/login', {
    method: 'POST',
    body: {
      username,
      password,
      device_fingerprint: fingerprint,
      device_cookie: deviceCookie || null,
    },
  });
}

export function verifyOtp({ adminId, otp, fingerprint, trustDevice }) {
  return request('/api/website-auth/verify-otp', {
    method: 'POST',
    body: {
      admin_id: adminId,
      otp,
      device_fingerprint: fingerprint,
      trust_device: !!trustDevice,
    },
  });
}

export function me() {
  return request('/api/website-auth/me', { auth: true });
}

export function logout() {
  return request('/api/website-auth/logout', { method: 'POST', auth: true });
}

/* ----------------------------- CMS ------------------------------ */

export function getPage(slug) {
  return request(`/api/website-cms/pages/${encodeURIComponent(slug)}`);
}

export function updatePage(slug, payload) {
  return request(`/api/website-cms/pages/${encodeURIComponent(slug)}`, {
    method: 'PUT',
    body: payload,
    auth: true,
  });
}

export function upload(file) {
  const fd = new FormData();
  fd.append('file', file);
  return request('/api/website-cms/upload', {
    method: 'POST',
    body: fd,
    isForm: true,
    auth: true,
  });
}

export function seed(pages) {
  return request('/api/website-cms/seed', {
    method: 'POST',
    body: { pages },
    auth: true,
  });
}

const api = {
  getApiBase,
  getToken,
  setToken,
  login,
  verifyOtp,
  me,
  logout,
  getPage,
  updatePage,
  upload,
  seed,
};

export default api;
